import type { LabeledValue } from 'antd/es/select'
import type { RawValue } from 'src/components/declare'
import type { EasyRemoteSearch } from 'src/components/common/declare'
import type { EditControlProps } from '../../declare'
import React, { useState, useEffect, useMemo, useRef, useContext } from 'react'
import { autorun, reaction } from 'mobx'
import { InfoCircleOutlined, PlusCircleOutlined } from '@ant-design/icons'
import { ListTableContext, EditContext, EasyRemoteSelect, EasyModal, FormPage, EditPage } from 'src/components'
import { appStore } from 'src/stores'
import { $api } from 'src/plugins'
import { getNavFieldsFromNavField, getSaveFieldFromNavField, getRulesFromNavFilters } from 'src/components/root/helper'
import { initFilter, extendOrders, initGroup, extendGroups, getSingleRule, extendRules, extendInclude } from 'src/utils/common'
import { guid } from 'src/utils/tools'
import { get, set } from 'lodash'

const ExtraRender = (props: {
  config: EditControlProps['config'],
  onClose: (data: any) => void
}) => {
  const edit = useContext(EditContext)

  const [extraSrcForm, setExtraSrcForm] = useState<any>()
  useEffect(() => reaction(
    () => get(edit.formBase, props.config.SHOW_ID_FIELD),
    formId => {
      const { SHOW_FORM_CODE: formCode } = props.config
      formCode && formId && setExtraSrcForm({ formCode, formId })
    }
  ), [props.config.SHOW_FORM_CODE, props.config.SHOW_ID_FIELD])

  // 配置了快捷编辑表单
  const extraEditForm = useMemo(() => {
    const { EDIT_FORM_CODE: formCode, EDIT_FORM_FILTER: field, EDIT_FORM_FILTER_VALUE: value } = props.config
    if (formCode) {
      const target: any = { formCode }
      // 分页下拉组件快捷编辑表单添加的过滤条件，通过此过滤条件，达到表单编辑的效果，而不是新增效果
      let payload = undefined
      if (field && value) {
        const { OrgId } = appStore.current.headers
        payload = initFilter({ OrgId })
        extendRules(payload, [getSingleRule(field, 1, value)])
      }
      payload && (target.formFilter = payload)
      return target
    }
    return null
  }, [props.config.EDIT_FORM_CODE, props.config.EDIT_FORM_FILTER, props.config.EDIT_FORM_FILTER_VALUE])

  return (
    <React.Fragment>
      {
        extraSrcForm
          ? (
            <EasyModal
              modal={{ width: '100%' }}
              slot={<InfoCircleOutlined className='fz-20 pd-l8' />}
            >
              <FormPage
                formCode={extraSrcForm.formCode}
                formData={{ formId: extraSrcForm.formId, formDisabled: true }}
              />
            </EasyModal>
          )
          : null
      }
      {
        extraEditForm
          ? (
            <EasyModal
              modal={{ title: extraEditForm.formCode, width: '100%' }}
              slot={<PlusCircleOutlined className='fz-20 pd-l8' />}
              onClose={props.onClose}
            >
              <EditPage formCode={extraEditForm.formCode} />
            </EasyModal>
          )
          : null
      }
    </React.Fragment>
  )
}

const PaginationSelect = (props: EditControlProps) => {
  const table = useContext(ListTableContext)
  const edit = useContext(EditContext)

  // 配置的过滤字段是否变化，变化则需要重新加载分页组件
  const [refreshKey, setRefreshKey] = useState(guid())
  useEffect(() => {
    let timer: any = -1
    const dispose = autorun(() => {
      const { Nav_Filters } = props.config
      const filters = getRulesFromNavFilters(Nav_Filters, {
        user: appStore.current.userinfo,
        formBase: edit.formBase,
        record: table?.selectedRow
      })
      clearTimeout(timer)
      timer = setTimeout(() => {
        filters.length && setRefreshKey(JSON.stringify(filters))
      }, 1000)
    })

    return () => {
      dispose()
      clearTimeout(timer)
    }
  }, [props.config.Nav_Filters])

  // 分页下拉数据
  const source = useRef([])
  const getSource = (params: EasyRemoteSearch) => {
    const {
      INPUT_DATA_API: url,
      INPUT_FILTER_ORG_TYPE: OrgType,
      INPUT_FILTER_FIELD,
      INPUT_NAV_FIELD,
      Nav_Filters,
      INPUT_SORT_FIELD,
      INPUT_SORT_ORDER,
    } = props.config
    if (!url) return new Promise(resolve => { resolve(false) })

    const { OrgId } = appStore.current.headers
    const payload = initFilter({ OrgId, OrgType, PageIndex: params.current })
    // 存在多字段排序
    const Orders: { Field: string, Order: number }[] = []
    if (INPUT_SORT_FIELD) {
      const sorts = INPUT_SORT_FIELD.split(',')
      const orders = INPUT_SORT_ORDER?.split(',') ?? []
      sorts.forEach((field: string, index: number) => {
        Orders.push({ Field: field, Order: Number(orders[index] ?? 0) })
      })
      extendOrders(payload, Orders)
    }
    // 搜索过滤字段
    const filterFields: string[] = INPUT_FILTER_FIELD?.split(',') || ['NAME', 'CODE']
    const value = params.value ?? ''
    const Groups: any = initGroup(false)
    String(value) && Groups.Rules.push(...filterFields.map(field => getSingleRule(field, 9, value as RawValue)))
    extendGroups(payload, [Groups])
    // navFields 导航字段
    const navFields = getNavFieldsFromNavField(INPUT_NAV_FIELD)
    navFields.length && extendInclude(payload, navFields)
    // 配置的过滤字段
    const filters = getRulesFromNavFilters(Nav_Filters, {
      user: appStore.current.userinfo,
      formBase: edit.formBase,
      record: table?.selectedRow
    })
    extendRules(payload, filters)

    return $api['app/getDataByPost'](payload, {
      url,
      fullData: true
    }).then((res: any) => {
      source.current = res?.Data ?? []
      return res
    })
  }

  // 字段配置，逻辑是通过 INPUT_SAVE_FIELD 获取到 Select 的 value 值 
  const fieldConfig = useMemo(() => {
    let key = 'ID'
    let label = 'NAME'

    const { INPUT_NAV_FIELD: nav, INPUT_SAVE_FIELD: save } = props.config
    if (nav && save) {
      const navs = nav.split(',')
      const saves = save.split(',')
      navs.forEach((nav: string, index: number) => {
        nav === 'ID' && saves[index] && (key = saves[index])
        nav === 'NAME' && saves[index] && (label = saves[index])
      })
    }

    return { key, label }
  }, [props.config.INPUT_NAV_FIELD, props.config.INPUT_SAVE_FIELD])

  const handleChange = (data: LabeledValue[]) => {
    const { FIELD_NAME, INPUT_ID_FIELD } = props.config
    const target = data.map(val => {
      const find = props.value?.find((item: any) => get(item, fieldConfig.key) === val.value)
      if (find) return find

      const extraFields = getSaveFieldFromNavField(val.value, source.current, props.config)
      set(extraFields, 'ID', guid())
      set(extraFields, 'ORG_ID', get(edit.formBase, 'ORG_ID') || appStore.current.headers.OrgId)
      if (INPUT_ID_FIELD) {
        const fields = FIELD_NAME.split('.')
        const targetField = fields.length > 1 ? fields.splice(0, fields.length - 1).join('.') : 'ID'
        set(extraFields, INPUT_ID_FIELD, get(edit.formBase, targetField))
      }
      return extraFields
    })
    // 表单数据
    props.onChange?.(target)
  }

  // 分页下拉组件快捷编辑表单关闭后回调
  const handleExtraClose = (data: any) => {
    data?.ID && getSource({ value: data.NAME, current: 1 })
  }

  // 下拉显示字段，用于 Option 的 label 显示
  const optionLabelFields = useMemo(() => {
    return props.config.INPUT_SHOW_FIELD?.split(',') ?? ['NAME']
  }, [props.config.INPUT_SHOW_FIELD])

  // 回填显示字段，用于选中后回填回 Select 框
  const customLabelFields = useMemo(() => {
    return props.config.INPUT_LABEL_FIELD?.split(',')
  }, [props.config.INPUT_LABEL_FIELD])

  const selectedValue = useMemo(() => {
    return props.value?.map((item: any) => {
      const label = customLabelFields.map((label: string) => get(item, label)).filter((la: string) => !!la)
      return {
        label: label.length ? label.join('-') : undefined,
        value: get(item, fieldConfig.key),
      }
    })
  }, [props.value, props.config.FIELD_NAME, fieldConfig, customLabelFields])

  const { __READONLY__, EDITABLE, PROMPT_INFO } = props.config
  return (
    <EasyRemoteSelect
      multipleMode
      key={refreshKey}
      value={selectedValue}
      remoteMethod={getSource}
      onChange={val => handleChange(val as LabeledValue[])}
      selectProps={{
        disabled: __READONLY__ || !EDITABLE || props.evtConfig?.disabled,
        autoClearSearchValue: false,
        placeholder: PROMPT_INFO
      }}
      fieldOption={{ labels: optionLabelFields }}
      addonAfter={<ExtraRender config={props.config} onClose={handleExtraClose} />}
      className='full-width'
    />
  )
}

export default PaginationSelect